/**
 * The round card's Max Camera Distance check: each player's SWTOR value
 * against the round's limit (as the in-game slider shows it), and a switch to
 * let a player who can't change it play without the check.
 */

import { memo } from 'react';
import {
  cameraCheckPlayerIds,
  cameraCheckStatus,
  formatCameraDistance,
  roundMaxCameraDistance,
  type CameraCheckStatus,
  type Game,
  type Player,
  type Round,
} from '@swtorbit/shared-socket-events';

const STATUS_BADGES: Record<Exclude<CameraCheckStatus, 'none'>, { className: string; label: string }> = {
  ok: { className: 'badge badge--green', label: 'OK' },
  over: { className: 'badge badge--red', label: 'TOO FAR' },
  unknown: { className: 'badge badge--amber', label: 'NO VALUE' },
  off: { className: 'badge badge--slate', label: 'CHECK OFF' },
};

function describeValue(player: Player): string {
  if (!player.cameraDistance) return 'not reported';
  return player.cameraDistance.value === null ? 'game default (not set)' : formatCameraDistance(player.cameraDistance.value);
}

interface CameraCheckSectionProps {
  game: Game;
  round: Round;
  roundIndex: number;
  onSetCheck: (roundIndex: number, playerId: string, checked: boolean) => void;
}

export const CameraCheckSection = memo(function CameraCheckSection({ game, round, roundIndex, onSetCheck }: CameraCheckSectionProps) {
  const limit = roundMaxCameraDistance(round);
  if (limit === null || (round.status !== 'setup' && round.status !== 'hiding' && round.status !== 'seeking')) return null;

  const players = cameraCheckPlayerIds(game, round)
    .map((playerId) => game.players.find((p) => p.playerId === playerId))
    .filter((p): p is Player => !!p);

  return (
    <div className="mt-xs">
      <p className="fw-bold">Max Camera Distance: {formatCameraDistance(limit)} or less</p>
      {players.length === 0 ? (
        <p className="text-slate fs-sm">No connected players to check yet.</p>
      ) : (
        <div className="d-flex flex-column gap-2xs">
          {players.map((player) => {
            const status = cameraCheckStatus(round, player);
            if (status === 'none') return null;
            const badge = STATUS_BADGES[status];
            const checkOff = status === 'off';
            return (
              <div key={player.playerId} className="round-player">
                <span>{player.characterName ?? `Player ${player.playerId.slice(-4)}`}</span>
                <span className={badge.className}>{badge.label}</span>
                <span className="text-slate fs-sm">{describeValue(player)}</span>
                <button
                  className="btn btn--sm btn--outline btn--slate ms-auto"
                  onClick={() => onSetCheck(roundIndex, player.playerId, checkOff)}
                  title={checkOff ? 'Check this player\'s camera distance again' : 'Let this player play without the camera check'}
                >
                  {checkOff ? 'Check again' : 'Switch check off'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
